// Home for a new sign-up with no business yet: say hello, what happens next
// (GET /me/next) and the ways to start telling us about the business.
import { useEffect } from 'react';
import { MessageCircle, MessageSquare, Phone } from 'lucide-react';
import { displayPhone, firstName } from '@cover/core/format.ts';
import { useChat } from '../components/Chat.tsx';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { Card, CardBody } from '@cover/core/ui/Card.tsx';
import { WhatsNext } from '../components/WhatsNext.tsx';
import { useSession } from '../session.tsx';

export function WelcomePage() {
  const { me } = useSession();
  const brand = useBrand();
  const chat = useChat();
  const wa = whatsappLink(brand, 'Hi, I’d like to get my business covered');
  useEffect(() => {
    document.title = `Welcome · ${brand.name}`;
  }, [brand.name]);
  const ways = [
    { onClick: () => chat.open(), icon: MessageSquare, label: 'Chat here', hint: 'Right in your dashboard' },
    wa && displayPhone(me.contact?.phoneMasked) && { href: wa, icon: MessageCircle, label: 'Chat on WhatsApp', hint: 'From your phone' },
    brand.contact.phone && { href: `tel:${brand.contact.phone.replace(/\s/g, '')}`, icon: Phone, label: `Call ${brand.contact.phone}`, hint: 'Talk it through' },
  ].filter(Boolean) as { href?: string; onClick?: () => void; icon: typeof Phone; label: string; hint: string }[];
  return (
    <>
      <div className="mb-6 lg:mb-8">
        <p className="text-sm font-medium text-mid">Welcome to {brand.name}</p>
        <h1 tabIndex={-1} className="truncate text-[28px] font-bold leading-tight tracking-tight focus:outline-none sm:text-4xl">
          {firstName(me.contact?.displayName) ? `Hi ${firstName(me.contact?.displayName)}` : 'Hello'}
        </h1>
        <p className="mt-2 max-w-prose text-mid">Tell us a little about your work and we’ll work out what cover a business like yours needs, then price it. It takes a few minutes.</p>
      </div>
      <div className="space-y-6">
        <WhatsNext />
        <Card>
          <CardBody>
            <h2 className="mb-3 font-semibold">Start whichever way suits you</h2>
            <ul className="grid gap-3 sm:grid-cols-3">
              {ways.map((w) => (
                <li key={w.label}>
                  {w.onClick ? (
                    <button type="button" onClick={w.onClick} className={wayCls + ' w-full text-left'}>
                      <WayInner w={w} />
                    </button>
                  ) : (
                    <a href={w.href} className={wayCls}>
                      <WayInner w={w} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

const wayCls = 'flex h-full flex-col gap-3 rounded-card p-4 text-ink no-underline ring-1 ring-inset ring-line hover:bg-surface hover:no-underline';

function WayInner({ w }: { w: { icon: typeof Phone; label: string; hint: string } }) {
  return (
    <>
      <span className="flex h-10 w-10 items-center justify-center rounded-control bg-brand/10 text-brand">
        <w.icon className="h-5 w-5" aria-hidden />
      </span>
      <span>
        <span className="block font-semibold">{w.label}</span>
        <span className="block text-sm text-mid">{w.hint}</span>
      </span>
    </>
  );
}
